import * as Sentry from '@sentry/react';
import { Box, Card, Flex, VStack } from '@televet/kibble-ui';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { useMemo, useState } from 'react';
import Badge from 'shared/components/Badges/Badge';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { TwilioPhoneNumberOrigin } from 'shared/enums/TwilioPhoneNumberOrigin';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  PhoneNumberStatus,
  ServiceCapability,
  ServiceProvider,
  useCreateOneClinicPhoneNumberMutation,
  useGetClinicPhoneNumbersQuery,
  useGetClinicSettingsConversationsAllActiveTwilioNumbersQuery,
  useUpdateClinicSettingsConversationsTransferTwilioNumberMutation,
  useUpdateOneClinicPhoneNumberMutation,
} from 'shared/types/graphql';
import { ITwilioIncomingPhoneNumber } from 'shared/types/twilio';
import { formatPhoneNumber } from 'shared/utils';

interface IConfigurePhoneNumbersModalProps {
  onSuccess: () => void;
  onClose: () => void;
}

const ConfigurePhoneNumbersModal = ({ onSuccess, onClose }: IConfigurePhoneNumbersModalProps): JSX.Element => {
  const [selectedNumber, setSelectedNumber] = useState<ITwilioIncomingPhoneNumber | null>(null);
  const toast = useToast();

  const { data: allActiveTwilioNumbersData, loading: activeTwilioNumbersLoading } =
    useGetClinicSettingsConversationsAllActiveTwilioNumbersQuery({
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  const availablePhoneNumbers = useMemo(
    () =>
      allActiveTwilioNumbersData?.getTwilioNumbers?.payload?.filter(
        (numberData: ITwilioIncomingPhoneNumber) => numberData.status === 'in-use',
      ),
    [allActiveTwilioNumbersData],
  );

  const { currentClinicId } = useClinicUser();

  const { data: phoneNumbersData, refetch: refetchPhoneNumbers } = useGetClinicPhoneNumbersQuery({
    variables: { where: { clinicId: { equals: currentClinicId }, capability: { equals: ServiceCapability.Sms } } },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const [createOneClinicPhoneNumber] = useCreateOneClinicPhoneNumberMutation();
  const [updateOneClinicPhoneNumber] = useUpdateOneClinicPhoneNumberMutation();

  const [transferTwilioNumber, { loading: isTransferringTwilioNumber }] =
    useUpdateClinicSettingsConversationsTransferTwilioNumberMutation();

  const handleAddNumberClick = async (): Promise<void> => {
    if (!selectedNumber) return;

    const { sid, phoneNumber } = selectedNumber;
    const formattedNumber = formatPhoneNumber(phoneNumber);
    const clinicPhoneNumbers = phoneNumbersData?.findManyClinicPhoneNumber?.map((phoneNumber) => phoneNumber.number);
    try {
      await transferTwilioNumber({ variables: { phoneNumberSid: sid } });

      if (clinicPhoneNumbers?.includes(phoneNumber)) {
        await updateOneClinicPhoneNumber({
          variables: {
            where: {
              capabilityAndNumberAndProvider: {
                capability: ServiceCapability.Sms,
                number: phoneNumber,
                provider: ServiceProvider.Twilio,
              },
            },
            data: { isDeleted: false },
          },
        });
      } else {
        await createOneClinicPhoneNumber({
          variables: {
            data: {
              capability: ServiceCapability.Sms,
              provider: ServiceProvider.Twilio,
              isPrimary: false,
              number: phoneNumber,
              status: PhoneNumberStatus.Hosted,
              clinic: { connect: { id: currentClinicId } },
            },
          },
        });
      }
      await refetchPhoneNumbers();

      toast({
        status: 'success',
        title: `Success! ${formattedNumber} was added to the clinic`,
      });
      onSuccess();
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        title: `Whoops! An error occurred while adding ${formattedNumber} to the clinic. Please try again.`,
      });
      console.error(e);
    } finally {
      onClose();
    }
  };

  return (
    <Modal onClose={onClose} size="lg" isOpen={true} isCentered={true} scrollBehavior="inside">
      <ModalHeader>
        Add Phone Number
        <Text as="p" fontWeight="normal" mt={2}>
          Select from the available active numbers below to begin using this number for text messaging.
        </Text>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody py={3}>
        <Box height="260px">
          {activeTwilioNumbersLoading ? (
            <Spinner />
          ) : (
            <VStack alignItems="stretch" paddingBottom={5} spacing={3}>
              {availablePhoneNumbers?.map((number: ITwilioIncomingPhoneNumber) => {
                const { sid, phoneNumber, friendlyName, origin } = number;
                return (
                  <Card
                    key={sid}
                    p={2}
                    borderWidth="1px"
                    borderRadius="md"
                    isInteractive={true}
                    onClick={(): void => setSelectedNumber(number)}
                    isActive={selectedNumber?.sid === sid}
                  >
                    <Flex alignItems="center" gap={2} fontWeight="semibold">
                      <Box>{formatPhoneNumber(phoneNumber)}</Box>
                      {origin === TwilioPhoneNumberOrigin.Hosted ? <Badge>Hosted</Badge> : null}
                    </Flex>
                    <Text>{friendlyName}</Text>
                  </Card>
                );
              })}
            </VStack>
          )}
        </Box>
      </ModalBody>
      <ModalFooterButtons
        onCancel={onClose}
        onSubmit={handleAddNumberClick}
        isLoading={isTransferringTwilioNumber}
        submitText="Add Number"
        shadow="mdR"
        gap={2}
        isDisabled={!availablePhoneNumbers?.length || !selectedNumber}
      />
    </Modal>
  );
};

export default ConfigurePhoneNumbersModal;
